import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { IconButton } from '@sdb/planning-ui';

import { Table, TableBody, TableCell, TableHead } from '@mui/material';

import {
  NumericTableCell,
  TableHeaderRow,
  ZebraStripedTableRow
} from 'components/shared/Table';

import {
  CorrectionField,
  DetailGroupBlock,
  DetailGroupHeader,
  DetailGroupTitle,
  DetailTableTools
} from '../../EmploymentHours.styles';
import TotalHoursValue from './TotalHoursValue';

import type { SortableDetailTableProps } from './HoursRowDetailPanel.types';

/** Wide enough for five characters — '12,24' — plus the correction input's number spinner. */
const VALUE_WIDTH = 88;
const CORRECTION_WIDTH = 120;

/**
 * One group of the month as an outlined card: a heading, then its `label | value | correction` rows.
 * The two number columns are the same width in every card, so every amount in the window lines up.
 *
 * Draggable and removable within its section, like the dashboard tiles on the home page
 * (`roster-portal/home/quality-monitor/SortableTask.tsx`): the handle and the remove button stay
 * invisible until the block is hovered or focused, as they do there.
 */
const SortableDetailTable = ({
  group,
  title,
  rows,
  row: month,
  showPlusMinus,
  onRemove,
  labels
}: SortableDetailTableProps) => {
  'use memo';

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: group });

  const hasCorrections = rows.some(
    (row) => row.correction || row.correctionName
  );

  return (
    <DetailGroupBlock
      ref={setNodeRef}
      variant="outlined"
      isDragging={isDragging}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <DetailGroupHeader>
        <DetailGroupTitle>{title}</DetailGroupTitle>
        <DetailTableTools className="detail-table-tools">
          <IconButton
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            type="grip-dots-vertical"
            color="secondary"
            size="small"
            aria-label={labels.reorder}
          />
          <IconButton
            type="xmark"
            color="secondary"
            size="small"
            aria-label={labels.remove}
            onClick={onRemove}
          />
        </DetailTableTools>
      </DetailGroupHeader>
      <Table size="small">
        {/* the correction column only appears where there is a correction to show */}
        {hasCorrections && (
          <TableHead>
            <TableHeaderRow>
              <TableCell />
              <NumericTableCell width={VALUE_WIDTH}>
                {labels.value}
              </NumericTableCell>
              <NumericTableCell width={CORRECTION_WIDTH}>
                {labels.correction}
              </NumericTableCell>
            </TableHeaderRow>
          </TableHead>
        )}
        <TableBody>
          {rows.map((row) => (
            <ZebraStripedTableRow key={row.id}>
              <TableCell>{row.label}</TableCell>
              {/* absent values stay blank, matching the grid */}
              <NumericTableCell width={VALUE_WIDTH}>
                {row.totalOf ? (
                  <TotalHoursValue
                    base={month[row.totalOf] as number | undefined}
                    showPlusMinus={showPlusMinus}
                  />
                ) : (
                  row.value
                )}
              </NumericTableCell>
              {hasCorrections && (
                <NumericTableCell width={CORRECTION_WIDTH}>
                  {row.correctionName ? (
                    <CorrectionField
                      name={row.correctionName}
                      // slotProps here replaces ControlledNumericField's default wholesale, so its
                      // own `step: 0.01` has to be repeated or decimal corrections fail to submit
                      slotProps={{
                        htmlInput: {
                          step: 0.01,
                          // several blocks label their rows with a bare year, so the name needs the
                          // block too — 'Correction Statutory leave 2026', not 'Correction 2026'
                          'aria-label': `${labels.correction} ${title} ${row.label}`
                        }
                      }}
                    />
                  ) : (
                    row.correction
                  )}
                </NumericTableCell>
              )}
            </ZebraStripedTableRow>
          ))}
        </TableBody>
      </Table>
    </DetailGroupBlock>
  );
};

export default SortableDetailTable;
